import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconCode, IconBug, IconExplain, IconGenerate, IconOptimize, IconFormat, IconArrowRight } from '../components/Icons';
import './Docs.css';

/* ── Doc sections ── */
const SECTIONS = [
  { id: 'overview',    label: 'Overview' },
  { id: 'free',        label: 'Free to Use' },
  { id: 'donations',   label: 'Donations' },
  { id: 'code-assist', label: 'Code Assist' },
  { id: 'commands',    label: 'Commands' },
  { id: 'web-ui',      label: 'Web UI' },
  { id: 'privacy',     label: 'Privacy' },
  { id: 'faq',         label: 'FAQ' },
];

const COMMANDS = [
  { name: '/debug',    params: '<code> [language]', Icon: IconBug,      color: '#ef4444', desc: 'Paste a code snippet and Code Assist will identify the bug, explain what is wrong, and return the corrected version.' },
  { name: '/explain',  params: '<code> [language]', Icon: IconExplain,  color: '#3b82f6', desc: 'Get a clear, structured explanation of what a piece of code does — line by line if needed.' },
  { name: '/generate', params: '<description>',     Icon: IconGenerate, color: '#8b5cf6', desc: 'Describe what you want built and Code Assist writes the code for you. Supports any language.' },
  { name: '/optimize', params: '<code> [language]', Icon: IconOptimize, color: '#f59e0b', desc: 'Paste code that works but could be better. Code Assist rewrites it for performance, readability, or both.' },
  { name: '/format',   params: '<code> [language]', Icon: IconFormat,   color: '#10b981', desc: 'Clean up messy or inconsistently styled code. Follows the standard style guide for each language.' },
];

const FAQ = [
  { q: 'Is Code Assist really free?', a: 'Yes. There are no subscriptions, no premium tiers, and no paywalls. Every feature is available to every user at no cost.' },
  { q: 'What AI does Code Assist use?', a: 'Code Assist runs on a proprietary AI model built and managed by PERM Systems. It is not powered by OpenAI, Anthropic, or any third-party provider.' },
  { q: 'Does Code Assist store my code?', a: 'No. Code inputs are processed in real time and not stored long-term. PERM Systems does not sell data or share it with third parties.' },
  { q: 'Where do donations go?', a: '100% of donations are passed directly to The Water Project, a non-profit that builds clean water infrastructure in sub-Saharan Africa. PERM Systems keeps nothing.' },
  { q: 'How do I donate?', a: 'Donations can be made directly at thewaterproject.org. PERM Systems does not process donations — we simply encourage users who want to give back to support The Water Project directly.' },
  { q: 'Can I request a custom bot for my server?', a: 'No. PERM Systems does not build custom bots for individual servers or organisations. All products are built for the community as a whole. You can suggest ideas via the contact page.' },
  { q: 'What languages does Code Assist support?', a: 'Python, JavaScript, TypeScript, Rust, Go, Java, C, C++, C#, Ruby, PHP, Swift, Kotlin, HTML, CSS, SQL, Bash, R, Lua, Dart, and more.' },
  { q: 'How do I get support?', a: 'Join the PERM Systems Discord server or use the contact page. The founder responds to every message personally.' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="docs-section">
      <h2 className="docs-section__title">{title}</h2>
      <div className="docs-section__body">{children}</div>
    </section>
  );
}

function Callout({ type = 'info', children }) {
  const icons = { info: 'ℹ', tip: '✦', warning: '⚠' };
  return (
    <div className={`docs-callout docs-callout--${type}`}>
      <span className="docs-callout__icon">{icons[type]}</span>
      <div>{children}</div>
    </div>
  );
}

export default function Docs() {
  const [activeSection, setActive] = useState('overview');

  const scrollTo = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="page docs-page">
      <div className="docs-layout">

        {/* ── Sidebar nav ── */}
        <aside className="docs-sidebar">
          <div className="docs-sidebar__header">
            <span className="eyebrow" style={{ marginBottom: 0 }}>Documentation</span>
            <p className="docs-sidebar__version">PERM Systems · 2026</p>
          </div>
          <nav className="docs-nav">
            {SECTIONS.map(s => (
              <button
                key={s.id}
                className={`docs-nav__item${activeSection === s.id ? ' active' : ''}`}
                onClick={() => scrollTo(s.id)}
              >
                {s.label}
              </button>
            ))}
          </nav>
          <div className="docs-sidebar__footer">
            <a href="https://discord.gg/permsystems" target="_blank" rel="noopener noreferrer"
               className="docs-sidebar__support-link">
              Get support on Discord →
            </a>
          </div>
        </aside>

        {/* ── Content ── */}
        <main className="docs-content">

          <Section id="overview" title="Overview">
            <p>PERM Systems builds free, AI-powered developer tools for Discord. This documentation covers everything you need to know — how the products work, our commitment to keeping them free, and how your donations make a difference.</p>
            <div className="docs-cards">
              <div className="docs-card">
                <div className="docs-card__icon"><IconCode size={20} color="var(--blue-dark)"/></div>
                <h4>Code Assist</h4>
                <p>AI coding bot for Discord. Debug, explain, generate, optimize, and format code with slash commands.</p>
                <Link to="/code-assist/home" className="docs-card__link">Learn more <IconArrowRight size={12}/></Link>
              </div>
              <div className="docs-card">
                <div className="docs-card__icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 17s-6-4.5-6-9a6 6 0 0 1 12 0c0 4.5-6 9-6 9z" fill="rgba(96,165,250,0.15)" stroke="#60a5fa" strokeWidth="1.6"/></svg>
                </div>
                <h4>The Water Project</h4>
                <p>All user donations go to The Water Project — clean water infrastructure in sub-Saharan Africa.</p>
                <a href="https://thewaterproject.org" target="_blank" rel="noopener noreferrer" className="docs-card__link">Visit site <IconArrowRight size={12}/></a>
              </div>
            </div>
          </Section>

          <Section id="free" title="Free to Use">
            <p>Every product built by PERM Systems is completely free. This is not a freemium model — there are no paid tiers, no premium features, and no paywalls of any kind.</p>

            <Callout type="tip">You do not need an account, a subscription, or a payment method to use any PERM Systems product.</Callout>

            <h3>What "free" means here</h3>
            <ul>
              <li>All Discord bot commands are available to every user in every server.</li>
              <li>The Code Assist web UI at <Link to="/code-assist/use/home">/code-assist/use/home</Link> is fully accessible with no login required.</li>
              <li>There are no rate-limited free tiers. Usage is not metered or capped per user.</li>
              <li>Future products will also be free.</li>
            </ul>

            <h3>How is this sustainable?</h3>
            <p>PERM Systems is a solo-operated project run by a single developer. Operational costs are kept intentionally low. The project grows through community support, word of mouth, and the intrinsic motivation to build tools that actually help people — not through monetisation.</p>
          </Section>

          <Section id="donations" title="Donations">
            <p>PERM Systems accepts no revenue. When users choose to donate, those funds are contributed entirely to <a href="https://thewaterproject.org" target="_blank" rel="noopener noreferrer" className="docs-link">The Water Project</a> — a non-profit that finances and builds sustainable clean water projects in sub-Saharan Africa.</p>

            <Callout type="info">PERM Systems keeps 0% of donations. Every cent goes directly to The Water Project.</Callout>

            <h3>About The Water Project</h3>
            <p>The Water Project is a US-based non-profit organisation that provides access to clean, safe water across sub-Saharan Africa. They work with local communities to build and maintain wells, rainwater collection systems, and sand dams. Since 2006 they have served hundreds of thousands of people.</p>
            <p>You can learn more and donate directly at:</p>
            <a href="https://thewaterproject.org" target="_blank" rel="noopener noreferrer" className="docs-donate-btn">
              Visit The Water Project →
            </a>

            <h3>Why The Water Project?</h3>
            <p>Access to clean water is one of the most fundamental and impactful things that can be improved globally. PERM Systems operates in the technology space — where most users have access to the internet, devices, and tools. Donating to an organisation that addresses one of the most basic human needs felt like the most meaningful way to give back.</p>

            <Callout type="tip">Donations are made directly through The Water Project's website. PERM Systems does not process, hold, or handle any funds.</Callout>
          </Section>

          <Section id="code-assist" title="Code Assist">
            <p>Code Assist is an AI-powered coding bot for Discord, launched in 2026. It runs on a fully custom AI model built and managed by PERM Systems — not OpenAI, Anthropic, or any third-party provider.</p>

            <h3>What it does</h3>
            <ul>
              <li>Debugs code and identifies bugs with explanations and fixes</li>
              <li>Explains what code does in plain English</li>
              <li>Generates code from natural language descriptions</li>
              <li>Optimizes code for performance and readability</li>
              <li>Formats code to standard style conventions</li>
            </ul>

            <h3>Supported languages</h3>
            <p>Python, JavaScript, TypeScript, Rust, Go, Java, C, C++, C#, Ruby, PHP, Swift, Kotlin, HTML, CSS, SQL, Bash, R, Lua, Dart, and more.</p>

            <h3>How to add it to your server</h3>
            <ol>
              <li>Go to the <Link to="/code-assist/invite" className="docs-link">invite page</Link>.</li>
              <li>Select your Discord server from the dropdown.</li>
              <li>Authorise the required permissions (Send Messages, Use Slash Commands, Read Message History).</li>
              <li>Type any slash command in your server to start using it immediately.</li>
            </ol>

            <Callout type="info">Code Assist requires no setup, configuration files, or dashboards. Invite it and start using it instantly.</Callout>
          </Section>

          <Section id="commands" title="Commands">
            <p>All Code Assist commands are Discord slash commands. Type <code>/</code> in any channel where the bot has access to see the available commands.</p>

            <div className="docs-commands">
              {COMMANDS.map(({ name, params, Icon, color, desc }) => (
                <div key={name} className="docs-command">
                  <div className="docs-command__header">
                    <span className="docs-command__icon" style={{ color }}>
                      <Icon size={16}/>
                    </span>
                    <code className="docs-command__name">{name}</code>
                    <span className="docs-command__params">{params}</span>
                  </div>
                  <p className="docs-command__desc">{desc}</p>
                </div>
              ))}
            </div>

            <Callout type="tip">The <code>[language]</code> parameter is optional. Code Assist can usually detect the language automatically from the code snippet.</Callout>
          </Section>

          <Section id="web-ui" title="Web UI">
            <p>In addition to the Discord bot, Code Assist has a full web interface at <Link to="/code-assist/use/home" className="docs-link">permsystems.com/code-assist/use/home</Link>.</p>

            <h3>Chat modes</h3>
            <p>The web UI has six modes, each with its own AI behaviour:</p>
            <ul>
              <li><strong>Chat</strong> — General coding conversation. Ask anything code-related.</li>
              <li><strong>Debug</strong> — Structured bug analysis: identify, explain, fix, summarise.</li>
              <li><strong>Explain</strong> — Line-by-line walkthrough of what code does.</li>
              <li><strong>Generate</strong> — Write code from a description.</li>
              <li><strong>Optimize</strong> — Rewrite code to be faster, cleaner, or more idiomatic.</li>
              <li><strong>Format</strong> — Reformat code to the standard style guide for its language.</li>
            </ul>

            <h3>Code Runner</h3>
            <p>The Code Runner tab provides a split-pane editor and terminal. Paste code, select a language, and click Run Code. The AI simulates execution and returns the expected terminal output — including error tracebacks.</p>

            <Callout type="warning">The Code Runner uses AI to simulate execution. Output is realistic but not guaranteed to match a real runtime exactly. For production debugging, always test in an actual environment.</Callout>

            <h3>Chat history</h3>
            <p>The web UI automatically saves your conversations to your browser's local storage. Access previous chats from the History tab in the sidebar. Sessions are stored locally — they are never uploaded to PERM Systems servers.</p>

            <h3>Mode suggestions</h3>
            <p>After you send a message, the AI analyses your request and suggests switching to a more appropriate mode if it detects a better fit. You can accept or dismiss the suggestion — it never switches automatically.</p>
          </Section>

          <Section id="privacy" title="Privacy">
            <p>PERM Systems takes a minimal-data approach. The goal is to process what is needed to respond to your request, and nothing more.</p>

            <h3>What is collected</h3>
            <ul>
              <li>Code snippets and messages sent to the bot or web UI are processed in real time to generate responses.</li>
              <li>Inputs are not stored long-term after a response is generated.</li>
              <li>No personal identifiers are required to use any product.</li>
            </ul>

            <h3>What is not collected</h3>
            <ul>
              <li>PERM Systems does not sell data to advertisers or third parties.</li>
              <li>Code you submit is not used to train AI models.</li>
              <li>No analytics or tracking scripts run on this website beyond basic server logs.</li>
            </ul>

            <Callout type="info">Web UI chat history is stored entirely in your own browser via localStorage. It never leaves your device.</Callout>

            <p>For full details, see the <Link to="/privacy" className="docs-link">Privacy Policy</Link> and <Link to="/terms" className="docs-link">Terms of Service</Link>.</p>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="docs-faq">
              {FAQ.map(({ q, a }) => (
                <details key={q} className="docs-faq__item">
                  <summary className="docs-faq__question">{q}</summary>
                  <p className="docs-faq__answer">{a}</p>
                </details>
              ))}
            </div>

            <div className="docs-still-stuck">
              <h3>Still have questions?</h3>
              <p>Join the Discord or send a message through the contact page. Every question gets a personal response.</p>
              <div className="docs-still-stuck__actions">
                <a href="https://discord.gg/permsystems" target="_blank" rel="noopener noreferrer" className="btn btn--dark btn--lg">
                  Join Discord
                </a>
                <Link to="/contact" className="btn btn--outline btn--lg">Contact Us</Link>
              </div>
            </div>
          </Section>

        </main>
      </div>
    </div>
  );
}
